import axios from "axios";
import { useUser } from "../contexts/UserContext";
import { useSpinner } from "../contexts/LoadingContext";
import { useNotification } from "../contexts/NotificationProvider";
import { useNavigate } from "react-router-dom"; // Import the useNavigate hook

export const useAxios = () => {
  const { token, logout } = useUser(); // Assuming you have a logout method in your context
  const { showSpinner, hideSpinner } = useSpinner(); // Access spinner control functions
  const notify = useNotification();
  const navigate = useNavigate(); // Initialize navigation hook

  const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL, 
    timeout: 120000, // Set a timeout if needed
  });

  axiosInstance.interceptors.request.use(
    (config) => {
      showSpinner();
      if (token) {
        config.headers["Authorization"] = `token ${token}`;
      }

      if (!(config.data instanceof FormData)) {
        config.headers["Content-Type"] = "application/json";
      }
      return config;
    },
    (error) => {
      hideSpinner();
      notify({
        type: "error",
        message: "Error",
        description: error.response?.data?.error ?? error.message,
      });
      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.response.use(
    (response) => {
      hideSpinner();
      return response;
    },
    (error) => {
      hideSpinner();
      const { status, data } = error.response ?? {};
      if (status === 401) {
        // Handle Unauthorized error
        logout(false); // Optionally clear user data or token
        notify({
          type: "error",
          message: error.request.responseURL?.includes("login")
            ? "User couldn't log-in"
            : "Session Expired",
          description: error.request.responseURL?.includes("login")
            ? data?.detail
            : "Your session has expired. Please log in again.",
        });
        navigate("/login"); // Redirect to login page
        return Promise.reject(error.response.data);
      }

      if (data?.errors?.length) {
        // Case where 'errors' array exists in data
        notify({
          type: "error",
          description: (
            <>
              <p>
                Your request couldn't be completed due to the following errors:
              </p>
              <ul>
                <li style={{ textTransform: "capitalize" }}>
                  {data.errors[0]}
                </li>
              </ul>
            </>
          ),
        });
        return Promise.reject(error.response.data);
      } else if (data && typeof data === "object") {
        notify({
          type: "error",
          message: "Your request could not be completed",
          description: (
            <ul>
              {Object.values(data).map((messages, index) =>
                Array.isArray(messages) ? (
                  messages.map((msg, msgIndex) => (
                    <li
                      style={{ textTransform: "capitalize" }}
                      key={`${index}-${msgIndex}`}
                    >
                      {msg}
                    </li>
                  ))
                ) : (
                  <li style={{ textTransform: "capitalize" }} key={index}>
                    {messages}
                  </li>
                )
              )}
            </ul>
          ),
        });
        return Promise.reject(error.response.data);
      } else {
        // Default case where a single error message is available
        notify({
          type: "error",
          message: "Error",
          description: data?.message ?? error.message,
        });
      }

      return Promise.reject(error.response.data);
    }
  );

  return { axiosInstance };
};
